import { apiService } from '@/shared/services/apiServices';
import serviceRequestEndpoints from './serviceRequestEndpoints';

export interface AcknowledgeServiceRequestPayload {
  status: 'accepted' | 'rejected';
  reason?: string;
}

export interface AcknowledgeServiceRequestData {
  serviceRequestId: string;
  status: string;
}

export interface AcknowledgeServiceRequestResult {
  error: boolean;
  data?: AcknowledgeServiceRequestData;
  status?: number;
  isNetworkError?: boolean;
  /** True when the backend returned 403 — the technician's privilege was revoked. */
  permissionDenied?: boolean;
  /** True when the backend returned 409 — the acknowledgement window has expired. */
  windowExpired?: boolean;
}

/**
 * Sends an accept or reject decision for an assigned task to the backend.
 *
 * @remarks
 * Shared by the assigned TECHNICIAN and the ADMIN / SUPER_ADMIN the task is
 * assigned to (RR-1106) — both hit `PATCH .../acknowledge` and the backend
 * resolves the caller from the auth token.
 *
 * HTTP 403 → sets `permissionDenied: true` (caller is not the assignee / privilege revoked).
 * HTTP 409 → sets `windowExpired: true` (SR no longer in ASSIGNED status).
 * The hook maps these flags to user-facing error messages.
 *
 * @param serviceRequestId - Internal UUID from the route path.
 * @param payload - `{ status, reason? }` — reason required when status = "rejected".
 */
export const acknowledgeServiceRequestApiService = async (
  serviceRequestId: string,
  payload: AcknowledgeServiceRequestPayload
): Promise<AcknowledgeServiceRequestResult> => {
  const endpoint =
    serviceRequestEndpoints.ACKNOWLEDGE_SERVICE_REQUEST(serviceRequestId);

  const response = await apiService.patchAPI(endpoint, payload);

  if (response.status === 403) {
    return { error: true, status: response.status, permissionDenied: true };
  }

  if (response.status === 409) {
    return { error: true, status: response.status, windowExpired: true };
  }

  const result: AcknowledgeServiceRequestResult = { error: response.error };
  if (response.data !== undefined) {
    result.data = response.data as AcknowledgeServiceRequestData;
  }
  if (response.status !== undefined) {
    result.status = response.status;
  }
  if (response.isNetworkError !== undefined) {
    result.isNetworkError = response.isNetworkError;
  }
  return result;
};
